import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { analyticsClient } from "../analytics/events";
import { enqueuePendingFunnelPageView, getPendingFunnelPageView } from "../analytics/funnel-pageview";
import { getQuizStartEventId } from "../analytics/quiz-start";
import { consentManager, type ConsentState } from "../privacy/consent";
import { loadFunnelAccess, type FunnelAccessState } from "../state/funnel-access";

const STORAGE_KEY = "conan.quiz.consent.v1";

function hasStoredChoice(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

interface NewlyGrantedDestinations {
  analytics: boolean;
  marketing: boolean;
}

function newlyGrantedDestinations(
  previous: ConsentState,
  current: ConsentState,
): NewlyGrantedDestinations {
  return {
    analytics: !previous.analytics && current.analytics,
    marketing: !previous.marketing && current.marketing,
  };
}

function replayNewlyGrantedProviders(
  granted: NewlyGrantedDestinations,
  access: FunnelAccessState | undefined,
  path: string,
): void {
  if (!granted.analytics && !granted.marketing) return;

  const isVariantB = path === "/b" || path.startsWith("/b/");
  // On /b the mounted landing owns delivery. Replaying here as well would
  // duplicate providers and could bind a fresh landing event to stale access
  // left in this tab from an older quiz session.
  const landingView = isVariantB && path === "/b/quiz" ? getPendingFunnelPageView("B") : undefined;
  if (landingView) {
    void analyticsClient.track("FunnelPageView", {
      eventId: landingView.eventId,
      ...(landingView.occurredAt ? { occurredAt: landingView.occurredAt } : {}),
      ...(landingView.pagePath ? { pagePath: landingView.pagePath } : {}),
      ...(landingView.metadata ? { metadata: landingView.metadata } : {}),
      firstParty: false,
    }, {
      firstParty: false,
      meta: granted.marketing,
      ga4: granted.analytics,
    });
  }

  const variant = path === "/b/quiz" ? "B" : path === "/quiz" ? "A" : undefined;
  if (!variant || !access) return;
  void analyticsClient.track("QuizStart", {
    eventId: getQuizStartEventId(access.sessionId),
    pagePath: path,
    metadata: { variant },
  }, {
    firstParty: false,
    meta: granted.marketing,
    ga4: granted.analytics,
  });
}

function replayFirstPartyAfterConsentSync(
  access: FunnelAccessState,
  path: string,
): void {
  const session = { sessionId: access.sessionId, sessionToken: access.sessionToken };
  analyticsClient.setSession(session);
  if (path === "/b/quiz") enqueuePendingFunnelPageView("B", session);

  const variant = path === "/b/quiz" ? "B" : path === "/quiz" ? "A" : undefined;
  if (!variant) return;
  void analyticsClient.track("QuizStart", {
    eventId: getQuizStartEventId(access.sessionId),
    pagePath: path,
    metadata: { variant },
  }, {
    firstParty: true,
    meta: false,
    ga4: false,
  });
}

export function ConsentBanner() {
  const [open, setOpen] = useState(() => !hasStoredChoice());
  const [analytics, setAnalytics] = useState(() => consentManager.getState().analytics);
  const [marketing, setMarketing] = useState(() => consentManager.getState().marketing);
  const [saving, setSaving] = useState(false);
  const [syncError, setSyncError] = useState(false);

  useEffect(() => {
    const reopen = () => {
      const current = consentManager.getState();
      setAnalytics(current.analytics);
      setMarketing(current.marketing);
      setSyncError(false);
      setOpen(true);
    };
    window.addEventListener("conan:open-consent", reopen);
    return () => window.removeEventListener("conan:open-consent", reopen);
  }, []);

  if (!open) return null;

  const save = async (choice: { analytics: boolean; marketing: boolean }) => {
    const retryingFailedSync = syncError;
    setSaving(true);
    setSyncError(false);
    const access = loadFunnelAccess();
    const path = window.location.pathname;
    const previous = consentManager.getState();
    const synchronization = consentManager.update(choice, access
      ? { sessionId: access.sessionId, sessionToken: access.sessionToken }
      : undefined);
    const current = consentManager.getState();
    const granted = newlyGrantedDestinations(previous, current);
    try {
      replayNewlyGrantedProviders(granted, access, path);
    } catch {
      // The privacy choice remains effective even if a measurement adapter is
      // unavailable. First-party queues and provider adapters retry separately.
    }
    try {
      await synchronization;
      if (access && choice.analytics && (granted.analytics || retryingFailedSync)) {
        replayFirstPartyAfterConsentSync(access, path);
      }
      setOpen(false);
    } catch {
      // The local choice is already effective. Keep the banner open so the
      // visitor can retry the authenticated server synchronization.
      setSyncError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside aria-busy={saving} className="consent-banner" aria-label="Preferências de privacidade">
      <div>
        <strong>Você escolhe como medimos esta experiência.</strong>
        <p>Cookies necessários mantêm o quiz e a retomada. Medição e publicidade só são ativadas com sua escolha. Respostas alimentares nunca são enviadas às plataformas de anúncios. Sua escolha vale neste domínio; o checkout abre em pay.hub.la, que usa tecnologias e regras próprias. <Link to="/privacidade">Saiba mais</Link>.</p>
      </div>
      <div className="consent-banner__choices">
        <label>
          <input checked={analytics} onChange={(event) => setAnalytics(event.target.checked)} type="checkbox" />
          Medição do funil
        </label>
        <label>
          <input checked={marketing} onChange={(event) => setMarketing(event.target.checked)} type="checkbox" />
          Publicidade personalizada
        </label>
      </div>
      <div className="consent-banner__actions">
        <button disabled={saving} onClick={() => void save({ analytics: false, marketing: false })} type="button">Somente necessário</button>
        <button disabled={saving} onClick={() => void save({ analytics, marketing })} type="button">Salvar escolhas</button>
        <button className="consent-save" disabled={saving} onClick={() => void save({ analytics: true, marketing: true })} type="button">{saving ? "Salvando…" : "Aceitar todos"}</button>
      </div>
      {syncError ? <p className="consent-banner__error" role="alert">Sua escolha já vale neste dispositivo, mas não foi sincronizada. Verifique a conexão e tente salvar novamente.</p> : null}
    </aside>
  );
}
